import { nanoid } from "nanoid";
import { useCallback } from "react";
import type { FpUserMessageCreate } from "../../../schemas/codegen";
import { useInputStore, useMessageStore } from "../../state/stores";
import { chatWebSocketManager } from "../websocket";
import { useActiveChatId } from "./useActiveChatId";

export function useSendChatMessage() {
  const activeChatId = useActiveChatId();

  // Get actions from stores (zustand functions are stable by default)
  const addMessage = useMessageStore((state) => state.addMessage);
  const clearInput = useInputStore((state) => state.clearInput);

  const sendMessage = useCallback(
    (content: string) => {
      if (!activeChatId) {
        console.error("No active chat ID for sending message");
        return;
      }

      const messageId = nanoid();
      const messageData: FpUserMessageCreate = {
        type: "user.message.create",
        messageId,
        content,
      };

      // Add message to state immediately
      addMessage({
        id: messageId,
        sender: "user",
        content,
        status: "completed",
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      // Clear input after message is added to state
      clearInput();

      // Send via the singleton manager
      const connection = chatWebSocketManager.getConnection(activeChatId);
      connection.send(messageData);
    },
    [activeChatId, addMessage, clearInput],
  );

  return sendMessage;
}
